import React, { useMemo } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useObjectSchema } from '../../hooks/useObjectSchema';
import { exampleFromSchema } from '../../utils/jsonSchema';
import { CodeHighlight } from '../CodeHighlight';

const ObjectJsonPreview: React.FC<{ objectId: string }> = ({ objectId }) => {
  const { schema, loading } = useObjectSchema(objectId);
  const json = useMemo(() => (schema ? JSON.stringify(exampleFromSchema(schema), null, 2) : ''), [schema]);

  return (
    <Box sx={{ p: 0.5, width: 420, maxWidth: '80vw' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Example of the JSON this object produces
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      ) : json ? (
        <CodeHighlight code={json} language="json" maxHeight={360} fontSize="0.75rem" />
      ) : (
        <Typography variant="caption" color="text.secondary">Preview unavailable for this object.</Typography>
      )}
    </Box>
  );
};

/** Wraps a label so hovering it shows an example JSON for the object. */
export const ObjectJsonTooltip: React.FC<{ objectId?: string | null; children: React.ReactNode }> = ({ objectId, children }) => {
  if (!objectId) return <>{children}</>;
  return (
    <Tooltip
      title={<ObjectJsonPreview objectId={objectId} />}
      placement="right-start"
      enterDelay={250}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: 4,
            maxWidth: 'none',
            p: 1,
          },
        },
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
        {children}
        <DataObjectIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Box>
    </Tooltip>
  );
};

export default ObjectJsonTooltip;
